import React, { useState, useEffect } from 'react';
import styles from '../styles/deleteUserConfirmationModal.module.css';

interface DeleteUserConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userEmail: string;
  userName?: string;
}

const DeleteUserConfirmationModal: React.FC<DeleteUserConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userEmail,
  userName
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [isValid, setIsValid] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmailInput('');
      setIsValid(false);
    }
  }, [isOpen]);

  // Check if email matches
  useEffect(() => {
    setIsValid(emailInput.trim().toLowerCase() === userEmail.toLowerCase());
  }, [emailInput, userEmail]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onConfirm();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>🚨 Delete User Confirmation</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.warningSection}>
            <div className={styles.warningIcon}>⚠️</div>
            <div className={styles.warningText}>
              <p><strong>This action will permanently delete the user and ALL their data:</strong></p>
              <ul>
                <li>User profile and account</li>
                <li>All progress data</li>
                <li>All inventory data (resentments, fears, harms)</li>
                <li>All feedback submissions</li>
                <li>All other user data</li>
              </ul>
              <p><strong>This action CANNOT be undone!</strong></p>
            </div>
          </div>

          <div className={styles.userInfo}>
            <p><strong>User to be deleted:</strong></p>
            <div className={styles.userDetails}>
              <div className={styles.userEmail}>{userEmail}</div>
              {userName && <div className={styles.userName}>{userName}</div>}
            </div>
          </div>

          <form onSubmit={handleSubmit} className={styles.confirmationForm}>
            <label htmlFor="email-confirmation" className={styles.inputLabel}>
              Type the user's email address to confirm deletion:
            </label>
            <input
              id="email-confirmation"
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Enter email address"
              className={`${styles.emailInput} ${emailInput && !isValid ? styles.invalid : ''}`}
              autoFocus
              autoComplete="off"
            />
            {emailInput && !isValid && (
              <div className={styles.errorMessage}>
                Email does not match. Please enter: {userEmail}
              </div>
            )}
          </form>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!isValid}
            className={`${styles.deleteButton} ${!isValid ? styles.disabled : ''}`}
          >
            🗑️ Delete User Permanently
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserConfirmationModal;













